import * as Sentry from '@sentry/react';
import { useCallback, useRef } from 'react';
import { getValidToken, refreshToken, isTokenExpired } from '../lib/auth';

interface FetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  retryOnAuthFailure?: boolean;
  maxRetries?: number;
}

interface AuthenticatedFetchResult<T> {
  data: T | null;
  error: string | null;
  status: number;
  ok: boolean;
}

export function useAuthenticatedFetch() {
  const isRefreshingRef = useRef(false);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string> | null> => {
    const token = await getValidToken();
    if (!token) return null;
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const handleTokenRefresh = useCallback(async (): Promise<string | null> => {
    if (isRefreshingRef.current && refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    isRefreshingRef.current = true;
    refreshPromiseRef.current = refreshToken();

    try {
      const newToken = await refreshPromiseRef.current;
      return newToken;
    } finally {
      isRefreshingRef.current = false;
      refreshPromiseRef.current = null;
    }
  }, []);

  const authenticatedFetch = useCallback(async <T = unknown>(
    url: string,
    options: FetchOptions = {}
  ): Promise<AuthenticatedFetchResult<T>> => {
    const {
      retryOnAuthFailure = true,
      maxRetries = 1,
      headers: customHeaders = {},
      ...fetchOptions
    } = options;

    let attempt = 0;

    while (attempt <= maxRetries) {
      try {
        const authHeaders = await getAuthHeaders();

        if (!authHeaders) {
          return {
            data: null,
            error: 'Not authenticated',
            status: 401,
            ok: false,
          };
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);
        const response = await fetch(url, {
          ...fetchOptions,
          headers: {
            ...authHeaders,
            ...customHeaders,
          },
          signal: fetchOptions.signal || controller.signal,
        });
        clearTimeout(timeoutId);

        if (response.status === 401 && retryOnAuthFailure && attempt < maxRetries) {
          if (isTokenExpired()) {
            return {
              data: null,
              error: 'Session expired. Please log in again.',
              status: 401,
              ok: false,
            };
          }

          const newToken = await handleTokenRefresh();
          if (newToken) {
            attempt++;
            continue;
          }

          return {
            data: null,
            error: 'Authentication failed. Please log in again.',
            status: 401,
            ok: false,
          };
        }

        let data: T | null = null;
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          try {
            data = await response.json();
          } catch {
            data = null;
          }
        }

        if (!response.ok) {
          const errorMessage = (data as { error?: string })?.error
            || (data as { message?: string })?.message
            || `Request failed with status ${response.status}`;

          if (response.status >= 500) {
            Sentry.captureMessage(errorMessage, { level: 'error', tags: { status: response.status, url } });
          }

          return {
            data,
            error: errorMessage,
            status: response.status,
            ok: false,
          };
        }

        return {
          data,
          error: null,
          status: response.status,
          ok: true,
        };
      } catch (error) {
        if (attempt >= maxRetries) {
          Sentry.captureException(error, { tags: { url, maxRetries } });
          return {
            data: null,
            error: error instanceof Error ? error.message : 'Network error',
            status: 0,
            ok: false,
          };
        }
        attempt++;
      }
    }

    return {
      data: null,
      error: 'Max retries exceeded',
      status: 0,
      ok: false,
    };
  }, [getAuthHeaders, handleTokenRefresh]);

  const get = useCallback(<T = unknown>(url: string, options?: FetchOptions) => {
    return authenticatedFetch<T>(url, { ...options, method: 'GET' });
  }, [authenticatedFetch]);

  const post = useCallback(<T = unknown>(url: string, body?: unknown, options?: FetchOptions) => {
    return authenticatedFetch<T>(url, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }, [authenticatedFetch]);

  const put = useCallback(<T = unknown>(url: string, body?: unknown, options?: FetchOptions) => {
    return authenticatedFetch<T>(url, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }, [authenticatedFetch]);

  const del = useCallback(<T = unknown>(url: string, options?: FetchOptions) => {
    return authenticatedFetch<T>(url, { ...options, method: 'DELETE' });
  }, [authenticatedFetch]);

  return {
    fetch: authenticatedFetch,
    get,
    post,
    put,
    delete: del,
    getAuthHeaders,
    refreshToken: handleTokenRefresh,
  };
}

export async function authenticatedFetch<T = unknown>(
  url: string,
  options: FetchOptions = {}
): Promise<AuthenticatedFetchResult<T>> {
  const {
    retryOnAuthFailure = true,
    maxRetries = 1,
    headers: customHeaders = {},
    ...fetchOptions
  } = options;

  let attempt = 0;

  while (attempt <= maxRetries) {
    try {
      const token = await getValidToken();

      if (!token) {
        return {
          data: null,
          error: 'Not authenticated',
          status: 401,
          ok: false,
        };
      }

      const authHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const controller2 = new AbortController();
      const timeoutId2 = setTimeout(() => controller2.abort(), 60000);
      const response = await fetch(url, {
        ...fetchOptions,
        headers: {
          ...authHeaders,
          ...customHeaders,
        },
        signal: fetchOptions.signal || controller2.signal,
      });
      clearTimeout(timeoutId2);

      if (response.status === 401 && retryOnAuthFailure && attempt < maxRetries) {
        if (isTokenExpired()) {
          return {
            data: null,
            error: 'Session expired. Please log in again.',
            status: 401,
            ok: false,
          };
        }

        const newToken = await refreshToken();
        if (newToken) {
          attempt++;
          continue;
        }

        return {
          data: null,
          error: 'Authentication failed. Please log in again.',
          status: 401,
          ok: false,
        };
      }

      let data: T | null = null;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        try {
          data = await response.json();
        } catch {
          data = null;
        }
      }

      if (!response.ok) {
        const errorMessage = (data as { error?: string })?.error
          || (data as { message?: string })?.message
          || `Request failed with status ${response.status}`;

        if (response.status >= 500) {
          Sentry.captureMessage(errorMessage, { level: 'error', tags: { status: response.status, url } });
        }

        return {
          data,
          error: errorMessage,
          status: response.status,
          ok: false,
        };
      }

      return {
        data,
        error: null,
        status: response.status,
        ok: true,
      };
    } catch (error) {
      if (attempt >= maxRetries) {
        Sentry.captureException(error, { tags: { url, maxRetries } });
        return {
          data: null,
          error: error instanceof Error ? error.message : 'Network error',
          status: 0,
          ok: false,
        };
      }
      attempt++;
    }
  }

  return {
    data: null,
    error: 'Max retries exceeded',
    status: 0,
    ok: false,
  };
}
